import { Badge, BadgeOrigin, BadgeProps, badgeClasses } from "@mui/material";
import { forwardRef, useMemo } from "react";

export interface AppBadgeProps extends Omit<BadgeProps, "anchorOrigin"> {
  // redefine to add new combinations like { vertical: "center", horizontal: "right"  }
  anchorOrigin?: {
    vertical?: "top" | "bottom" | "center";
    horizontal?: "left" | "right" | "center";
  };
  /** `medium` is default. */
  size?: "small" | "medium";
}

/** Extends MUI Badge. */
export default forwardRef<HTMLSpanElement, AppBadgeProps>(function AppBadge(
  { variant, anchorOrigin, size = "medium", children, ...otherProps }: AppBadgeProps,
  ref,
) {
  const anchorOriginNative = useMemo<BadgeOrigin | undefined>(
    () =>
      anchorOrigin
        ? {
            vertical: anchorOrigin.vertical != "center" ? anchorOrigin.vertical : undefined,
            horizontal: anchorOrigin.horizontal != "center" ? anchorOrigin.horizontal : undefined,
          }
        : undefined,
    [anchorOrigin?.vertical, anchorOrigin?.horizontal],
  );

  return (
    <Badge
      ref={ref}
      sx={{
        [`& .${badgeClasses.badge}`]: {
          ...(anchorOrigin?.vertical === "center" && {
            top: "50%",
            bottom: "unset",
          }),
          ...(anchorOrigin?.horizontal === "center" && {
            left: "50%",
            right: "unset",
          }),
          ...(anchorOrigin && {
            transform: `scale(1) translate(
                  ${anchorOrigin.horizontal === "left" || anchorOrigin.horizontal === "center" ? "-50%" : "50%"}, 
                  ${anchorOrigin.vertical === "top" || anchorOrigin.vertical === "center" ? "-50%" : "50%"})`,
          }),

          ...(size === "small" && {
            ...(variant === "standard" && {
              minWidth: 16,
              height: 16,
              padding: (th) => th.spacing(0, 0.5),
              pt: "1px", // compensate 1px diff between heigh and fontSize
              fontSize: (th) => th.typography.pxToRem(11),
              lineHeight: 1,
            }),
            ...(variant === "dot" && {}),
          }),
          ...(size === "medium" && {}),
        },
      }}
      variant={variant}
      anchorOrigin={anchorOriginNative}
      {...otherProps}
    >
      {children}
    </Badge>
  );
});
